import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  CodeBracketIcon,
  DocumentTextIcon,
  EllipsisVerticalIcon,
  PlayIcon,
} from '@heroicons/react/24/outline';
import type { Call } from '../../types/call';

interface CallRowActionsMenuProps {
  call: Call;
  onTranscript: () => void;
  onDetails: () => void;
  onRecording: () => void;
}

const MENU_WIDTH = 188;
const itemClass =
  'flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground hover:bg-muted';

const CallRowActionsMenu: React.FC<CallRowActionsMenuProps> = ({
  call,
  onTranscript,
  onDetails,
  onRecording,
}) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const hasDetails = Boolean(call.telnyx_call_control_id);
  const hasRecording = Boolean(call.recording_url || call.telnyx_call_control_id);

  const placeMenu = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const left = Math.max(8, Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8));
    const estimatedHeight = 148;
    const openUp = window.innerHeight - rect.bottom < estimatedHeight && rect.top > estimatedHeight;
    const top = openUp ? Math.max(8, rect.top - estimatedHeight - 6) : rect.bottom + 6;
    setPos({ top, left });
  };

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onClose = () => setOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onClose);
    window.addEventListener('scroll', onClose, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onClose);
      window.removeEventListener('scroll', onClose, true);
    };
  }, [open]);

  return (
    <div className="flex justify-end">
      <button
        ref={triggerRef}
        type="button"
        aria-label="Call actions"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          if (open) {
            setOpen(false);
            return;
          }
          placeMenu();
          setOpen(true);
        }}
        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
      >
        <EllipsisVerticalIcon className="h-5 w-5" />
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            aria-label="Call actions"
            style={{ top: pos.top, left: pos.left, width: MENU_WIDTH }}
            className="fixed z-[9999] overflow-hidden rounded-xl border border-border bg-card py-1 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              role="menuitem"
              className={itemClass}
              onClick={() => {
                setOpen(false);
                onTranscript();
              }}
            >
              <DocumentTextIcon className="h-4 w-4" />
              Transcript
            </button>
            {hasDetails && (
              <button
                type="button"
                role="menuitem"
                className={itemClass}
                onClick={() => {
                  setOpen(false);
                  onDetails();
                }}
              >
                <CodeBracketIcon className="h-4 w-4" />
                Call details
              </button>
            )}
            {hasRecording && (
              <button
                type="button"
                role="menuitem"
                className={itemClass}
                onClick={() => {
                  setOpen(false);
                  onRecording();
                }}
              >
                <PlayIcon className="h-4 w-4" />
                Recording
              </button>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

export default CallRowActionsMenu;
